import BulletList from '@tiptap/extension-bullet-list';
import OrderedList from '@tiptap/extension-ordered-list';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Extension } from '@tiptap/core';
import { Decoration, DecorationSet } from '@tiptap/pm/view';
import { Plugin } from '@tiptap/pm/state';

// ─── Custom list extensions ────────────────────────────────────────────────────
export const CustomOrderedList = OrderedList.extend({
    addAttributes() {
        return {
            type: {
                default: '1',
                parseHTML: element => element.getAttribute('type'),
                renderHTML: attributes => ({ type: attributes.type }),
            },
        };
    },
});

export const CustomBulletList = BulletList.extend({
    addAttributes() {
        return {
            style: {
                default: 'disc',
                parseHTML: element => element.style.listStyleType || 'disc',
                renderHTML: attributes => ({ style: `list-style-type: ${attributes.style}` }),
            },
        };
    },
});

// ─── Custom table cell / header (preserves class + data-raci + bgcolor) ───────
export const CustomTableCell = TableCell.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            class: {
                default: null,
                parseHTML: element => element.getAttribute('class'),
                renderHTML: attributes => attributes.class ? { class: attributes.class } : {},
            },
            'data-raci': {
                default: null,
                parseHTML: element => element.getAttribute('data-raci'),
                renderHTML: attributes => attributes['data-raci'] ? { 'data-raci': attributes['data-raci'] } : {},
            },
            bgcolor: {
                default: null,
                parseHTML: element => element.style.backgroundColor || null,
                renderHTML: attributes => attributes.bgcolor ? { style: `background-color: ${attributes.bgcolor}` } : {},
            },
        };
    },
});

export const CustomTableHeader = TableHeader.extend({
    addAttributes() {
        return {
            ...this.parent?.(),
            class: {
                default: null,
                parseHTML: element => element.getAttribute('class'),
                renderHTML: attributes => attributes.class ? { class: attributes.class } : {},
            },
        };
    },
});

// ─── RACI decoration plugin ───────────────────────────────────────────────────
function getRaciDecorations(doc) {
    const decorations = [];
    doc.descendants((node, pos) => {
        if (node.type.name === 'tableCell' || node.type.name === 'tableHeader') {
            const text = node.textContent?.trim().toUpperCase();
            if (/^[RACI]$/.test(text)) {
                const lower = text.toLowerCase();
                decorations.push(Decoration.node(pos, pos + node.nodeSize, { class: `raci-cell raci-${lower}`, 'data-raci': text }));
            }
        }
    });
    return DecorationSet.create(doc, decorations);
}

export const RaciDecorationExtension = Extension.create({
    name: 'raciDecoration',
    addProseMirrorPlugins() {
        return [
            new Plugin({
                state: {
                    init(_, { doc }) { return getRaciDecorations(doc); },
                    apply(tr, oldState) { return tr.docChanged ? getRaciDecorations(tr.doc) : oldState; },
                },
                props: {
                    decorations(state) { return this.getState(state); },
                },
            }),
        ];
    },
});

// ─── RACI color DOM utility ───────────────────────────────────────────────────
export const applyRaciColors = (el) => {
    if (!el) return;
    const tables = el.querySelectorAll('table');
    tables.forEach(table => {
        let isRaci = false;
        const rows = table.querySelectorAll('tr');
        if (rows.length === 0) return;

        const firstRowCells = rows[0].querySelectorAll('th, td');
        const colMapping = {};
        firstRowCells.forEach((cell, idx) => {
            const txt = cell.textContent?.trim().toUpperCase() || '';
            if (txt.includes('R (') || txt === 'R' || txt.includes('(RESPONSIBLE)')) { colMapping[idx] = 'r'; isRaci = true; }
            else if (txt.includes('A (') || txt === 'A' || txt.includes('(ACCOUNTABLE)')) { colMapping[idx] = 'a'; isRaci = true; }
            else if (txt.includes('C (') || txt === 'C' || txt.includes('(CONSULTED)')) { colMapping[idx] = 'c'; isRaci = true; }
            else if (txt.includes('I (') || txt === 'I' || txt.includes('(INFORMED)')) { colMapping[idx] = 'i'; isRaci = true; }
        });

        rows.forEach((row, rowIdx) => {
            const cells = row.querySelectorAll('th, td');
            cells.forEach((cell, colIdx) => {
                const text = cell.textContent?.trim().toUpperCase();
                
                // Determine target state
                let targetRaci = null;
                let targetClasses = [];
                
                if (rowIdx > 0) {
                    if (/^[RACI]$/.test(text)) {
                        isRaci = true;
                        const lw = text.toLowerCase();
                        targetRaci = text;
                        targetClasses = ['raci-cell', `raci-${lw}`];
                    } else if (colIdx in colMapping && text !== '' && text !== '—') {
                        const lw = colMapping[colIdx];
                        targetRaci = lw.toUpperCase();
                        targetClasses = ['raci-cell', `raci-${lw}`];
                    }
                }
                
                // 1. Check classes
                const raciClasses = ['raci-cell', 'raci-r', 'raci-a', 'raci-c', 'raci-i'];
                const currentClasses = Array.from(cell.classList);
                
                // We want to make sure the cell has all of targetClasses, and NONE of the other raciClasses
                const classesToRemove = raciClasses.filter(cls => !targetClasses.includes(cls) && currentClasses.includes(cls));
                const classesToAdd = targetClasses.filter(cls => !currentClasses.includes(cls));
                
                if (classesToRemove.length > 0) {
                    cell.classList.remove(...classesToRemove);
                }
                if (classesToAdd.length > 0) {
                    cell.classList.add(...classesToAdd);
                }
                
                // 2. Check data-raci attribute
                const currentRaci = cell.getAttribute('data-raci');
                if (targetRaci !== currentRaci) {
                    if (targetRaci === null) {
                        cell.removeAttribute('data-raci');
                    } else {
                        cell.setAttribute('data-raci', targetRaci);
                    }
                }
            });
        });

        // 3. Update table
        const hasRaciClass = table.classList.contains('raci-table');
        const currentTableType = table.getAttribute('data-table-type');
        
        if (isRaci) {
            if (!hasRaciClass) table.classList.add('raci-table');
            if (currentTableType !== 'raci') table.setAttribute('data-table-type', 'raci');
        } else {
            if (hasRaciClass) table.classList.remove('raci-table');
            if (currentTableType !== null) table.removeAttribute('data-table-type');
        }
    });
};
